import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SettingsIcon from '@mui/icons-material/Settings';
import ReplayIcon from '@mui/icons-material/Replay';
import {
    dropConnection,
    getConnection,
    getCurrentRun,
    listSources,
    listSyncFiles,
    retryAllFailedFiles,
    retryFailedFile,
    updateSource,
    type CloudSyncConnection,
    type CloudSyncFile,
    type CloudSyncRun,
    type CloudSyncSource,
    CLOUD_SYNC_PROVIDERS,
    type CloudSyncProvider,
} from '@wwf/sdk';
import { format } from 'date-fns';
import { useGetList, useNotify, useRefresh } from 'react-admin';
import { isActiveConnection, PROVIDER_LABELS } from './cloudSyncConstants';
import { useCloudSyncSetupDrawer } from './CloudSyncSetupDrawer';

const MEMBER_TYPES = ['VOLUNTEER', 'MAIN_VOLUNTEER', 'STAFF'] as const;

function formatSyncDate(value: string | null | undefined) {
    return value ? format(new Date(value), 'PPp') : '—';
}

function isRetryableIngestFile(file: CloudSyncFile) {
    return file.ingestStatus === 'FAILED';
}

function formatIngestStatus(file: CloudSyncFile) {
    if (file.ingestStatus === 'PENDING' && file.errorMessage) {
        return 'RETRY SCHEDULED';
    }
    return file.ingestStatus;
}

export const cloudSyncQueryKey = ['cloudSync'] as const;

type CloudSyncPageData = {
    activeProvider: CloudSyncProvider | null;
    connection: CloudSyncConnection | null;
    sources: CloudSyncSource[];
    run: CloudSyncRun | null;
    files: CloudSyncFile[];
};

async function fetchCloudSyncData(): Promise<CloudSyncPageData> {
    let provider: CloudSyncProvider | null = null;
    let activeConnection: CloudSyncConnection | null = null;

    for (const candidate of CLOUD_SYNC_PROVIDERS) {
        const candidateConnection = await getConnection(candidate);
        if (isActiveConnection(candidateConnection)) {
            provider = candidate;
            activeConnection = candidateConnection;
            break;
        }
    }

    if (provider) {
        const [srcs, currentRun, filePage] = await Promise.all([
            listSources(provider),
            getCurrentRun(provider),
            listSyncFiles(provider),
        ]);
        return {
            activeProvider: provider,
            connection: activeConnection,
            sources: srcs,
            run: currentRun,
            files: filePage.content ?? [],
        };
    }

    return {
        activeProvider: null,
        connection: null,
        sources: [],
        run: null,
        files: [],
    };
}

export const CloudSyncPage = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const queryClient = useQueryClient();
    const [confirmDropOpen, setConfirmDropOpen] = useState(false);
    const [dropping, setDropping] = useState(false);
    const [retryingAll, setRetryingAll] = useState(false);

    const { data: antennas = [] } = useGetList('antenna', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'name', order: 'ASC' },
    });

    const { data, error: queryError } = useQuery({
        queryKey: cloudSyncQueryKey,
        queryFn: fetchCloudSyncData,
    });

    const activeProvider = data?.activeProvider ?? null;
    const connection = data?.connection ?? null;
    const sources = data?.sources ?? [];
    const run = data?.run ?? null;
    const files = data?.files ?? [];
    const error = queryError
        ? queryError instanceof Error
            ? queryError.message
            : 'Failed to load sync data'
        : null;

    const { openSetupDrawer } = useCloudSyncSetupDrawer(async () => {
        refresh();
    });

    const progress =
        run && run.totalFiles > 0
            ? Math.round((run.processedFiles / run.totalFiles) * 100)
            : 0;

    const retryableFiles = files.filter(isRetryableIngestFile);

    const reloadSyncData = () => {
        void queryClient.invalidateQueries({ queryKey: cloudSyncQueryKey });
        refresh();
    };

    const handleRetryAllFailed = async () => {
        if (!activeProvider) return;
        setRetryingAll(true);
        try {
            const { retriedCount } = await retryAllFailedFiles(activeProvider);
            notify(
                retriedCount > 0
                    ? `Restarted ingestion for ${retriedCount} failed file${retriedCount === 1 ? '' : 's'}`
                    : 'No failed files to retry',
                { type: retriedCount > 0 ? 'success' : 'info' }
            );
            reloadSyncData();
        } catch (e) {
            notify(
                e instanceof Error
                    ? e.message
                    : 'Failed to restart failed file ingestion',
                { type: 'error' }
            );
        } finally {
            setRetryingAll(false);
        }
    };

    const handleDropConnection = async () => {
        if (!activeProvider) return;
        setDropping(true);
        try {
            await dropConnection(activeProvider);
            notify('Cloud connection removed', { type: 'success' });
            setConfirmDropOpen(false);
            refresh();
        } catch (e) {
            notify(
                e instanceof Error
                    ? e.message
                    : 'Failed to drop cloud connection',
                { type: 'error' }
            );
        } finally {
            setDropping(false);
        }
    };

    return (
        <Box p={2}>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                mb={2}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <CloudSyncIcon />
                    <Typography variant="h5">Cloud synchronisation</Typography>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    onClick={() =>
                        openSetupDrawer(
                            activeProvider
                                ? { provider: activeProvider }
                                : undefined
                        )
                    }
                >
                    {activeProvider ? 'Configure sync' : 'Set up cloud sync'}
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {connection?.status === 'NEEDS_RECONNECT' && activeProvider && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() =>
                                openSetupDrawer({ provider: activeProvider })
                            }
                        >
                            Reconnect
                        </Button>
                    }
                >
                    {PROVIDER_LABELS[activeProvider]} synchronisation is paused.{' '}
                    {connection.lastAuthError}. After reconnecting, only changes
                    since the last sync will be processed (no full re-sync).
                </Alert>
            )}

            {!activeProvider && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            No cloud provider connected
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Choose Google Drive or OneDrive in the setup wizard
                            to start synchronising documents.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {activeProvider && connection && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack
                            direction="row"
                            alignItems="flex-start"
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    {PROVIDER_LABELS[activeProvider]} connection
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Status: {connection.status}
                                </Typography>
                                {connection.accountEmail && (
                                    <Typography variant="body2">
                                        Account: {connection.accountEmail}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                color="error"
                                variant="outlined"
                                startIcon={<LinkOffIcon />}
                                onClick={() => setConfirmDropOpen(true)}
                                disabled={dropping}
                            >
                                Drop connection
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={confirmDropOpen}
                onClose={() => !dropping && setConfirmDropOpen(false)}
            >
                <DialogTitle>Drop cloud connection?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will disconnect{' '}
                        {activeProvider
                            ? PROVIDER_LABELS[activeProvider]
                            : 'the cloud provider'}{' '}
                        and permanently delete all synchronized data from our
                        database. Documents will no longer be available in the
                        chatbot knowledge base.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDropOpen(false)}
                        disabled={dropping}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => void handleDropConnection()}
                        disabled={dropping}
                    >
                        {dropping ? 'Dropping…' : 'Drop connection'}
                    </Button>
                </DialogActions>
            </Dialog>

            {activeProvider && run && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Sync progress
                        </Typography>
                        <Typography variant="body2">
                            {run.processedFiles} / {run.totalFiles} processed (
                            {run.failedFiles} failed) — {run.status}
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ mt: 1 }}
                        />
                    </CardContent>
                </Card>
            )}

            {activeProvider && (
                <>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Source mapping
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Kind</TableCell>
                                        <TableCell>Access level</TableCell>
                                        <TableCell>Antenna</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sources.map(source => (
                                        <SourceRow
                                            key={source.id}
                                            source={source}
                                            antennas={antennas}
                                            onSaved={() => refresh()}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography variant="h6">
                                    Ingested files
                                </Typography>
                                {retryableFiles.length > 0 && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ReplayIcon />}
                                        onClick={() =>
                                            void handleRetryAllFailed()
                                        }
                                        disabled={retryingAll}
                                    >
                                        {retryingAll
                                            ? 'Retrying…'
                                            : `Retry all failed (${retryableFiles.length})`}
                                    </Button>
                                )}
                            </Stack>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Path</TableCell>
                                        <TableCell>Access</TableCell>
                                        <TableCell>Created</TableCell>
                                        <TableCell>Last updated</TableCell>
                                        <TableCell>Last synced</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Error</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {files.map(file => (
                                        <FileRow
                                            key={file.id}
                                            file={file}
                                            provider={activeProvider}
                                            onRetried={reloadSyncData}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
};

function FileRow({
    file,
    provider,
    onRetried,
}: {
    file: CloudSyncFile;
    provider: CloudSyncProvider;
    onRetried: () => void;
}) {
    const notify = useNotify();
    const [retrying, setRetrying] = useState(false);
    const isRetryable = isRetryableIngestFile(file);

    return (
        <TableRow>
            <TableCell>{file.name}</TableCell>
            <TableCell>{file.remotePath}</TableCell>
            <TableCell>
                {file.accessLevel}
                {file.antennaId ? ` (${file.antennaId})` : ''}
            </TableCell>
            <TableCell>{formatSyncDate(file.createdAt)}</TableCell>
            <TableCell>{formatSyncDate(file.updatedAt)}</TableCell>
            <TableCell>
                {file.lastSyncedAt
                    ? format(new Date(file.lastSyncedAt), 'PPp')
                    : '—'}
            </TableCell>
            <TableCell>{formatIngestStatus(file)}</TableCell>
            <TableCell>{file.errorMessage}</TableCell>
            <TableCell>
                {isRetryable && (
                    <Button
                        size="small"
                        startIcon={<ReplayIcon />}
                        disabled={retrying}
                        onClick={async () => {
                            setRetrying(true);
                            try {
                                await retryFailedFile(file.id, provider);
                                notify('Ingestion restarted', {
                                    type: 'success',
                                });
                                onRetried();
                            } catch (e) {
                                notify(
                                    e instanceof Error
                                        ? e.message
                                        : 'Failed to restart ingestion',
                                    { type: 'error' }
                                );
                            } finally {
                                setRetrying(false);
                            }
                        }}
                    >
                        {retrying ? 'Retrying…' : 'Retry'}
                    </Button>
                )}
            </TableCell>
        </TableRow>
    );
}

function SourceRow({
    source,
    antennas,
    onSaved,
}: {
    source: CloudSyncSource;
    antennas: Array<{ id: string; name: string }>;
    onSaved: () => void;
}) {
    const [accessLevel, setAccessLevel] = useState(source.accessLevel);
    const [antennaId, setAntennaId] = useState(source.antennaId ?? '');

    return (
        <TableRow>
            <TableCell>{source.name}</TableCell>
            <TableCell>{source.kind}</TableCell>
            <TableCell>
                <TextField
                    select
                    size="small"
                    value={accessLevel}
                    onChange={e => setAccessLevel(e.target.value)}
                >
                    {MEMBER_TYPES.map(type => (
                        <MenuItem key={type} value={type}>
                            {type}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell>
                <TextField
                    select
                    size="small"
                    value={antennaId}
                    onChange={e => setAntennaId(e.target.value)}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="">All antennas</MenuItem>
                    {antennas.map(antenna => (
                        <MenuItem key={antenna.id} value={antenna.id}>
                            {antenna.name}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell>
                <Button
                    size="small"
                    onClick={async () => {
                        await updateSource(
                            source.id,
                            accessLevel,
                            antennaId || null
                        );
                        onSaved();
                    }}
                >
                    Save
                </Button>
            </TableCell>
        </TableRow>
    );
}
